// Konzern-Uebersicht als Tab in der Liquiditaetsplanung.
//
// Ersetzt die alte Standalone-Page LiquidityDashboardPage. Zeigt fuer alle
// konfigurierten Mandanten Anfangsbestand, Forecast Endbestand Dezember,
// Kreditrahmen-Auslastung und einen Mini-Chart ueber die 12 Monate.
//
// Datenquelle: dieselben /api/liquidity/dashboard- und /diagnostics-Endpoints,
// die auch die alte Page genutzt hat — der SAP-Cache wird zentral ueber
// `lib/liquidity/cache-refresh.ts` aktualisiert.

import { useEffect, useRef, useState } from 'react'
import { RefreshCw, TrendingDown } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { usePresentationStore } from '@/stores/presentation.ts'
import { Button, Card, IconButton, StatusBadge } from '@/components/ui'

type LiquidityStatus = 'OK' | 'DISPO' | 'KRITISCH' | 'ZAHLUNGSUNFÄHIG'

interface CompanySummary {
  company: { key: string; name: string; color: string; kreditrahmen: number }
  anfangsbestand: number
  endbestandDez: number
  kreditrahmen: number
  status: LiquidityStatus
  monthlyEndbestand: number[]
  monthlyStatus: LiquidityStatus[]
  cacheAge?: string
  lastUpdated?: string
  error?: string
}

interface DashboardData {
  companies: CompanySummary[]
  months: string[]
  konzernEndbestand: number
  konzernStatus: LiquidityStatus
}

const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

function formatEur(val: number, anonymize: boolean): string {
  return anonymize ? '•••' : EUR.format(val)
}

function MiniChart({ data, color, kreditrahmen, months, anonymize }: {
  data: number[]
  color: string
  kreditrahmen: number
  months?: string[]
  anonymize?: boolean
}) {
  const allVals = [...data, kreditrahmen ? -kreditrahmen : 0]
  const min = Math.min(...allVals)
  const max = Math.max(...allVals)
  const range = max - min || 1
  const h = 80, w = 500, pad = 10

  const pts = data.map((v, i) => {
    const x = pad + (i / Math.max(data.length - 1, 1)) * (w - 2 * pad)
    const y = pad + (h - 2 * pad) - ((v - min) / range) * (h - 2 * pad)
    return `${x},${y}`
  }).join(' ')

  const zeroY = pad + (h - 2 * pad) - ((0 - min) / range) * (h - 2 * pad)
  const kreditY = kreditrahmen > 0
    ? pad + (h - 2 * pad) - ((-kreditrahmen - min) / range) * (h - 2 * pad)
    : null

  const lineColor = anonymize ? '#22c55e' : color

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-20">
      <line x1={pad} y1={zeroY} x2={w - pad} y2={zeroY} stroke="#94a3b8" strokeWidth="0.5" strokeDasharray="4,4" />
      {kreditY !== null && (
        <line x1={pad} y1={kreditY} x2={w - pad} y2={kreditY} stroke="#ef4444" strokeWidth="0.5" strokeDasharray="4,4" />
      )}
      <polygon
        points={`${pad},${h - pad} ${pts} ${w - pad},${h - pad}`}
        fill={lineColor} fillOpacity="0.1"
      />
      <polyline points={pts} fill="none" stroke={lineColor} strokeWidth="2" vectorEffect="non-scaling-stroke" />
      {data.map((v, i) => {
        const x = pad + (i / Math.max(data.length - 1, 1)) * (w - 2 * pad)
        const y = pad + (h - 2 * pad) - ((v - min) / range) * (h - 2 * pad)
        const title = months?.[i] ? `${months[i]}: ${EUR.format(v)}` : ''
        return (
          <circle key={i} cx={x} cy={y} r="4"
            fill={anonymize ? '#22c55e' : v < 0 ? '#ef4444' : lineColor}
            vectorEffect="non-scaling-stroke" stroke="white" strokeWidth="1.5">
            {!anonymize && title && <title>{title}</title>}
          </circle>
        )
      })}
    </svg>
  )
}

export function KonzernOverviewTab() {
  const anonymize = usePresentationStore((s) => s.anonymize)

  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState<string | null>(null)
  const [refreshStatus, setRefreshStatus] = useState<string | null>(null)
  const [refreshErrors, setRefreshErrors] = useState<Array<{ key: string; error: string }>>([])
  const [refreshPct, setRefreshPct] = useState(0)
  const [refreshLabel, setRefreshLabel] = useState('')
  const autoRefreshTriggered = useRef(false)
  const [shouldAutoRefresh, setShouldAutoRefresh] = useState(false)

  function loadDashboard() {
    setLoading(true)
    return apiClient.get<DashboardData>('/liquidity/dashboard')
      .then((r) => {
        setData(r.data)
        setLoading(false)
        return r.data
      })
      .catch((err: unknown) => {
        setFetchError(err instanceof Error ? err.message : String(err))
        setLoading(false)
        return null
      })
  }

  useEffect(() => {
    void loadDashboard().then((d) => {
      if (d && d.companies.length > 0 && d.companies.every((c) => !c.lastUpdated)) {
        setShouldAutoRefresh(true)
      }
    })
  }, [])

  useEffect(() => {
    if (!refreshing) return
    const interval = setInterval(() => {
      apiClient.get<{ active: boolean; pct: number; label: string }>('/liquidity/refresh-progress')
        .then((r) => {
          if (r.data.active) {
            setRefreshPct(r.data.pct)
            setRefreshLabel(r.data.label || 'Läuft...')
          }
        })
        .catch(() => {})
    }, 1000)
    return () => clearInterval(interval)
  }, [refreshing])

  useEffect(() => {
    if (shouldAutoRefresh && !autoRefreshTriggered.current) {
      autoRefreshTriggered.current = true
      setShouldAutoRefresh(false)
      void handleRefreshAll()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoRefresh])

  async function handleRefreshAll() {
    setRefreshing('all')
    setRefreshPct(0)
    setRefreshLabel('Starte SAP-Abfragen...')
    setRefreshStatus(null)
    setRefreshErrors([])
    try {
      const resp = await apiClient.post<{ results: Array<{ key: string; success: boolean; error?: string }> }>(
        '/liquidity/refresh-all',
        {},
      )
      const results = resp.data.results ?? []
      const ok = results.filter((r) => r.success).length
      const failed = results.filter((r) => !r.success)
      setRefreshPct(100)
      setRefreshLabel('')
      setRefreshStatus(`${ok} Mandant${ok !== 1 ? 'en' : ''} aktualisiert${failed.length > 0 ? `, ${failed.length} fehlgeschlagen` : ''}`)
      setRefreshErrors(failed.map((r) => ({ key: r.key, error: r.error ?? 'Unbekannter Fehler' })))
      void loadDashboard()
    } catch (err: unknown) {
      setRefreshStatus(`Fehler: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setRefreshing(null)
      setTimeout(() => { setRefreshStatus(null); setRefreshPct(0); setRefreshErrors([]) }, 30000)
    }
  }

  async function handleRefreshCompany(companyKey: string, e: React.MouseEvent) {
    e.stopPropagation()
    setRefreshing(companyKey)
    setRefreshStatus(null)
    try {
      await apiClient.post(`/liquidity/refresh/${companyKey}`, {})
      setRefreshStatus(`${companyKey} aktualisiert`)
      void loadDashboard()
    } catch (err: unknown) {
      setRefreshStatus(`${companyKey}: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setRefreshing(null)
      setTimeout(() => setRefreshStatus(null), 5000)
    }
  }

  const konzernAnfang = data?.companies.reduce((s, c) => s + c.anfangsbestand, 0) ?? 0
  const konzernEndbestand = data?.konzernEndbestand ?? 0
  const konzernKredit = data?.companies.reduce((s, c) => s + c.kreditrahmen, 0) ?? 0

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-4 py-16">
        <div className="w-12 h-12 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
        <p className="text-gray-500">Daten werden geladen…</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-caption text-gray-500 inline-flex items-center gap-1.5">
          <TrendingDown className="size-4 text-brand-600" />
          Konzern-Forecast · {data?.months?.[0] ?? ''} – {data?.months?.[11] ?? ''} · SAP B1 Cache
        </p>
        <Button
          variant="primary"
          size="sm"
          onClick={() => void handleRefreshAll()}
          disabled={refreshing !== null}
        >
          <RefreshCw className={`size-4 mr-1 ${refreshing === 'all' ? 'animate-spin' : ''}`} />
          {refreshing === 'all' ? 'Läuft…' : 'Alle aktualisieren'}
        </Button>
      </div>

      {fetchError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">Fehler: {fetchError}</p>
        </div>
      )}

      {(refreshing || refreshStatus || refreshErrors.length > 0) && (
        <div className={`border rounded-lg p-4 ${refreshErrors.length > 0 ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
          {refreshing && (
            <div className="mb-2">
              <div className="flex items-center justify-between text-sm text-blue-800 mb-1.5">
                <span className="font-medium">{refreshLabel || 'Starte…'}</span>
                <span className="font-bold">{refreshPct}%</span>
              </div>
              <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all duration-300"
                  style={{ width: `${refreshPct}%` }}
                />
              </div>
            </div>
          )}
          {refreshStatus && (
            <p className={`text-sm ${refreshErrors.length > 0 ? 'text-red-700 font-medium' : refreshStatus.includes('Fehler') ? 'text-red-700' : 'text-green-700'}`}>
              {refreshStatus}
            </p>
          )}
          {refreshErrors.length > 0 && (
            <ul className="mt-2 space-y-1">
              {refreshErrors.map((e) => (
                <li key={e.key} className="text-xs text-red-700 bg-red-100 rounded px-2 py-1 font-mono break-all">
                  <span className="font-bold">{e.key}:</span> {e.error}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Card>
        <Card.Body>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-h3 font-semibold text-gray-900">Konzernübersicht</h2>
            {data && <StatusBadge status={data.konzernStatus} />}
          </div>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-500">Anfangsbestand</p>
              <p className={`text-2xl font-bold ${anonymize ? 'text-green-500' : konzernAnfang < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatEur(konzernAnfang, anonymize)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Prognose Endbestand Dez</p>
              <p className={`text-2xl font-bold ${anonymize ? 'text-green-500' : konzernEndbestand < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatEur(konzernEndbestand, anonymize)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Gesamter Kreditrahmen</p>
              <p className={`text-2xl font-bold ${anonymize ? 'text-green-500' : 'text-gray-900'}`}>
                {formatEur(konzernKredit, anonymize)}
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data?.companies.map((c) => (
          <Card key={c.company.key}>
            <Card.Body>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-2 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: c.company.color }} />
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-gray-800 truncate">
                      {anonymize ? c.company.key : c.company.name}
                    </h3>
                    <span className="text-[10px] text-gray-400">{c.cacheAge ?? 'Nie aktualisiert'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <StatusBadge status={c.status} />
                  <IconButton
                    icon={<RefreshCw className={`size-3.5 ${refreshing === c.company.key ? 'animate-spin' : ''}`} />}
                    aria-label={`${c.company.name} aktualisieren`}
                    variant="ghost"
                    size="xs"
                    onClick={(e) => void handleRefreshCompany(c.company.key, e)}
                    disabled={refreshing !== null}
                  />
                </div>
              </div>

              {c.error ? (
                <p className="text-sm text-amber-600 bg-amber-50 rounded p-3">{c.error}</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-[11px] text-gray-400">Anfangsbestand</p>
                      <p className={`text-base font-semibold ${anonymize ? 'text-green-500' : c.anfangsbestand < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatEur(c.anfangsbestand, anonymize)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-400">Endbestand Dez</p>
                      <p className={`text-base font-semibold ${anonymize ? 'text-green-500' : c.endbestandDez < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatEur(c.endbestandDez, anonymize)}
                      </p>
                    </div>
                  </div>

                  {c.company.kreditrahmen > 0 && !anonymize && (
                    <div className="mb-3">
                      <div className="flex justify-between text-[10px] text-gray-400 mb-0.5">
                        <span>Kredit: {EUR.format(c.company.kreditrahmen)}</span>
                        <span>
                          {c.endbestandDez < 0
                            ? `${Math.round((-c.endbestandDez / c.company.kreditrahmen) * 100)}% ausgelastet`
                            : '0%'}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            c.endbestandDez >= 0 ? 'bg-green-400'
                            : -c.endbestandDez / c.company.kreditrahmen > 0.9 ? 'bg-red-500'
                            : -c.endbestandDez / c.company.kreditrahmen > 0.7 ? 'bg-orange-400'
                            : 'bg-amber-400'
                          }`}
                          style={{ width: `${Math.min(100, c.endbestandDez >= 0 ? 0 : (-c.endbestandDez / c.company.kreditrahmen) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <MiniChart
                    data={c.monthlyEndbestand}
                    color={c.company.color}
                    kreditrahmen={anonymize ? 0 : c.company.kreditrahmen}
                    months={data?.months}
                    anonymize={anonymize}
                  />

                  <div className="flex gap-0.5 mt-1">
                    {c.monthlyStatus.map((s, i) => (
                      <div
                        key={i}
                        className={`flex-1 h-1 rounded-full ${
                          anonymize ? 'bg-green-400'
                          : s === 'OK' ? 'bg-green-400'
                          : s === 'DISPO' ? 'bg-amber-400'
                          : s === 'KRITISCH' ? 'bg-orange-400'
                          : 'bg-red-500'
                        }`}
                        title={!anonymize ? `${data?.months?.[i] ?? ''}: ${s}` : undefined}
                      />
                    ))}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        ))}
      </div>
    </div>
  )
}
